import * as Sauce from "Sauce";
import * as React from "react";
import { Position2, ParametricCurve2 } from "Math/Geometry";
import * as _ from "lodash";
import * as ReactDOM from "react-dom";
import * as d3 from "d3";
import * as $ from "jquery";
import * as Rx from "rxjs";
import { ChartMargins } from "UI/UIConstants";
import { Id } from "Sauce/Typeclass";

export type IdCurve = Id<ParametricCurve2>;
export interface PlotterProps {
    curves: IdCurve[];
    xmin: number;
    xmax: number;
    tmin: number;
    tmax: number;
    ymin: number;
    ymax: number;
    xAxisLabel?: string;
    yAxisLabe?: string;
    numSamples: number;
    margin?: { top: number, right: number, bottom: number, left: number };
    traceVertical?: boolean;
    traceHorizontal?: boolean
}

/* what should happen when we next draw to the chart */
interface curveUpdates {
        addCurves: IdCurve[],
        removeCurves: IdCurve[],
        updateCurves: IdCurve[]
}
interface State {
    curveUpdates: curveUpdates
}
export class Plotter extends Sauce.Component<PlotterProps, State> {
    private numSamples: number = 100;
    private domainSize: number;
    private margin = ChartMargins;
    private ow: number;
    private oh: number;
    private width: number;
    private height: number;
    constructor(props: PlotterProps) {
        super(props);
        if (props.numSamples > 0) {
            this.numSamples = props.numSamples;
        }
        if (props.margin) {
            this.margin = props.margin;
        }
        
        this.state = { curveUpdates: this.getIntersections([], props.curves ? props.curves : [])};
    }

    private getIntersections(prev: IdCurve[], next: IdCurve[]): curveUpdates {
        let removed = _.differenceBy(prev, next, v => v.key);
        let added = _.differenceBy(next, prev, v => v.key);
        let retained = _.differenceBy(next, added.concat(removed), v => v.key);

        return { 
            addCurves: added,
            removeCurves: removed,
            updateCurves: retained
        }
    }

    private sample(curve: ParametricCurve2): Position2[] {
        let interval = (this.props.tmax - this.props.tmin) / (this.numSamples - 1);
    
        let s: Position2[] = [];
        for (let i = 0; i < this.numSamples; i++) {
            let t = this.props.tmin + i * interval;
            s.push(new Position2(curve.x(t), curve.y(t)));
        }

        return s;
    }

    createChart(el: Element) {
        let svg = d3.select(el)
            .attr('class', 'geometry-plotter')
            .attr('width', '100%')
            .attr('height', '100%');
        
        let $svg = $(el).first();
        this.ow = $svg.width();
        this.oh = $svg.height();
        this.width = this.ow - this.margin.left - this.margin.right;
        this.height = this.oh - this.margin.top - this.margin.bottom;

        svg.append('g')
                .attr('class', 'x-axis-bottom')
                .attr('transform', 'translate(' + (this.margin.left) + ', ' + (this.oh - (this.margin.bottom )) + ')');
        svg.append('g')
                .attr('class', 'y-axis-left')
                .attr('transform', 'translate(' + (this.margin.left) + ', ' + (this.margin.top) + ')');

        svg.append('g')
            .attr('class', 'chart-canvas')
            .attr('transform', "translate(" + this.margin.left + "," + this.margin.top + ")")
            .attr("width", this.width)
            .attr("height", this.height)

        svg.append('svg')
            .attr('id', 'view-no-x-margins')
            .attr('y', this.margin.top)
            .attr('height', this.height)
        .append('g')
            .attr('class', 'no-x-margins');

        svg.append('svg')
            .attr('id', 'view-no-y-margins')
            .attr('x', this.margin.left)
            .attr('y', 0)
            .attr('width', this.width)
            .attr('height', "100%")
        .append('g')
            .attr('class', 'no-y-margins')

        this.updateChart(el);
    }
    private removeLines(el: Element, ids: string[]) {
        let svg = d3.select(el);
        let g = svg.select("g.chart-canvas");
        let no_x_margins = svg.select("g.no-x-margins");
        let no_y_margins = svg.select("g.no-y-margins");

        for(let id of ids) {
            let path = g.select("#" + id);
            let vtrace = no_y_margins.select("#vtrace-" + id);
            let htrace = no_x_margins.select("#htrace-" + id);

            path.remove();
            vtrace.remove();
            htrace.remove();
        }

    }

    private drawOneLine(el: Element, id: string, samples: Position2[]) {
        let svg = d3.select(el);
            
        let x = d3.scaleLinear()
            .domain([this.props.xmin, this.props.xmax])
            .range([0, this.width]);
    
        let x_no_margs = d3.scaleLinear()
            .domain([this.props.xmin, this.props.xmax])
            .range([0, this.ow]);

        let y = d3.scaleLinear()
            .domain([this.props.ymin, this.props.ymax])
            .range([this.height, 0]);

        let y_no_margs = d3.scaleLinear()
            .domain([this.props.ymin, this.props.ymax])
            .range([this.oh, 0]);

        let curve_no_x = d3.line<Position2>()
            .x(d => x_no_margs(d.x))
            .y(d => y(d.y));
    
        let curve_no_y = d3.line<Position2>()
            .x(d => x(d.x))
            .y(d => y_no_margs(d.y));
    
        let curve = d3.line<Position2>()
            .x(d => x(d.x))
            .y(d => y(d.y))
            .curve(d3.curveCatmullRom);
    
        let g = svg.select("g.chart-canvas");
        let no_x_margins = svg.select("g.no-x-margins");
        let no_y_margins = svg.select("g.no-y-margins");

        let path = g.select("#" + id);
        if (path.empty()) {
            path = g.append("path").attr("id", id);
        }

        let x_axis = d3.axisBottom(x)
            .ticks(Math.floor(this.width/30), "s");

        svg.select(".x-axis-bottom")
            .call(x_axis);

        let y_axis = d3.axisLeft(y)
            .ticks(Math.floor(this.height/30), "s");

        svg.select(".y-axis-left")
            .call(y_axis);

        if (this.props.traceVertical) {
            let tid = "vtrace-" + id;
            let vtrace = no_y_margins.select("#" + tid);
            if (vtrace.empty()) {
                vtrace = no_y_margins.append("path").attr("id", tid)
            }
            let xpos = samples[samples.length - 1].x;
            let ymin = this.props.ymin;
            let ymax = this.props.ymax;

            vtrace.datum([new Position2(xpos, ymin), new Position2(xpos, ymax)])
                .attr("d", curve_no_y)
                .attr("stroke", "black")
                .attr("fill", "none")
                .attr("stroke-width", 1);
        }

        if (this.props.traceHorizontal) {
            let tid = "htrace-" + id;
            let htrace = no_x_margins.select("#" + tid);
            if (htrace.empty()) {
                htrace = no_x_margins.append("path").attr("id", tid)
            }
            let ypos = samples[samples.length - 1].y;

            htrace.datum([new Position2(this.props.xmin, ypos), new Position2(this.props.xmax, ypos)])
                .attr("d", curve_no_x)
                .attr("stroke", "black")
                .attr("fill", "none")
                .attr("stroke-width", 1);
        }

        path.datum(samples)
            .attr("d", curve)
            .attr("stroke-width", 1)
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("stroke", "blue")
            .attr("fill", "none");
    }

    updateChart(el: Element) {

        this.removeLines(el, this.state.curveUpdates.removeCurves.map(c => c.key));
        
        this.state.curveUpdates.updateCurves.forEach(curve => {
            let samples = this.sample(curve.value);
            this.drawOneLine(el, curve.key, this.sample(curve.value));
        });
        this.state.curveUpdates.addCurves.forEach(curve => {
            let samples = this.sample(curve.value);
            this.drawOneLine(el, curve.key, this.sample(curve.value));
        })
    }
    componentDidMount() {
        this.createChart(ReactDOM.findDOMNode(this));
    }
    componentDidUpdate() {
        this.updateChart(ReactDOM.findDOMNode(this));
    }

    componentWillReceiveProps(newProps: PlotterProps) {
        this.setState({ curveUpdates: this.getIntersections(this.props.curves, newProps.curves) })
    }

    render() {
        let id = _.uniqueId("plot-");

        return <svg className={"geometry-plotter"}
        dangerouslySetInnerHTML={{__html: ""}}>

        </svg>
    }
}


// WEBPACK FOOTER //
// ./src/code/UI/Math/Plotter.tsx